import React, { useState } from 'react';
import { X, Check } from 'lucide-react';
import { useToast } from '../../Toast';

interface Contact {
  id: string;
  name: string;
  initials: string;
  email: string;
  phone: string;
  hasPortalAccess: boolean;
}

interface Document {
  id: string;
  name: string;
  category: 'consent' | 'questionnaire' | 'demographics' | 'scored';
  sharedOn?: string;
  status?: string;
  frequency?: string;
  lastShared?: string;
}

interface ShareIntakeModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientName: string;
  contacts: Contact[];
  onShare: (selectedContacts: string[], selectedDocs: string[], emailBody: string) => void;
}

const ShareIntakeModal: React.FC<ShareIntakeModalProps> = ({
  isOpen,
  onClose,
  clientName,
  contacts,
  onShare
}) => {
  const toast = useToast();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedContacts, setSelectedContacts] = useState<string[]>(
    contacts.length === 1 ? [contacts[0].id] : []
  );
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [emailBody, setEmailBody] = useState('');

  // Mock documents based on MyClinicCore structure
  const documents: Document[] = [
    // Consent Documents
    { id: 'consent-telehealth', name: 'Consent for Telehealth Consultation', category: 'consent', sharedOn: '09/21/2025', status: 'Signed on 09/22/2025' },
    { id: 'consent-ai', name: 'Consent for Use of Artificial Intelligence (AI) Tools in Therapy Services', category: 'consent', status: 'Not sent' },
    { id: 'consent-audio', name: 'Consent to Record Audio', category: 'consent', status: 'Not sent' },
    { id: 'consent-cc', name: 'Credit Card Authorization', category: 'consent', sharedOn: '09/21/2025', status: 'Signed on 09/22/2025' },
    { id: 'consent-informed', name: 'Informed Consent for Psychotherapy', category: 'consent', sharedOn: '09/21/2025', status: 'Signed on 09/22/2025' },
    { id: 'consent-privacy', name: 'Notice of Privacy Practices', category: 'consent', sharedOn: '09/21/2025', status: 'Signed on 09/22/2025' },
    { id: 'consent-policies', name: 'Practice Policies', category: 'consent', sharedOn: '09/21/2025', status: 'Signed on 09/22/2025' },

    // Scored Measures
    { id: 'scored-gad7', name: 'GAD-7', category: 'scored', frequency: 'Once', lastShared: '09/21/2025', status: 'Completed on 09/22/2025' },
    { id: 'scored-phq9', name: 'PHQ-9', category: 'scored', frequency: 'Once', lastShared: '09/21/2025', status: 'Completed on 09/22/2025' },

    // Questionnaires
    { id: 'questionnaire-minor', name: 'Consent for Minor Usage of Software Services', category: 'questionnaire', status: 'Not sent' },
    { id: 'questionnaire-covid', name: 'COVID-19 Pre-Appointment Screening Questionnaire', category: 'questionnaire', status: 'Not sent' },
    { id: 'questionnaire-release', name: 'Information Release Authorization', category: 'questionnaire', status: 'Not sent' },
    { id: 'questionnaire-intake', name: 'Standard Intake Questionnaire Template', category: 'questionnaire', sharedOn: '09/21/2025', status: 'Completed on 09/22/2025' },
    { id: 'questionnaire-financial', name: 'Third Party Financial Responsibility Form', category: 'questionnaire', status: 'Not sent' },

    // Demographics
    { id: 'demographics-cc', name: 'Credit card information', category: 'demographics', status: 'Not sent' },
    { id: 'demographics-form', name: 'Demographics form', category: 'demographics', sharedOn: '09/21/2025', status: 'Completed on 09/22/2025' }
  ];

  const toggleContact = (contactId: string) => {
    setSelectedContacts(prev =>
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const toggleDocument = (docId: string) => {
    setSelectedDocuments(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const toggleAllInCategory = (category: string) => {
    const categoryDocs = documents.filter(doc => doc.category === category);
    const categoryDocIds = categoryDocs.map(doc => doc.id);
    const allSelected = categoryDocIds.every(id => selectedDocuments.includes(id));

    if (allSelected) {
      setSelectedDocuments(prev => prev.filter(id => !categoryDocIds.includes(id)));
    } else {
      setSelectedDocuments(prev => Array.from(new Set([...prev, ...categoryDocIds])));
    }
  };

  const handleContinueFromStep1 = () => {
    if (selectedContacts.length === 0) {
      toast.warning('No Contact Selected', 'Please select at least one contact to share with.');
      return;
    }
    setStep(2);
  };

  const handleContinueToEmail = () => {
    if (selectedDocuments.length === 0) {
      toast.warning('No Document Selected', 'Please select at least one document to share.');
      return;
    }

    // Generate default email body
    const selectedContactNames = contacts
      .filter(c => selectedContacts.includes(c.id))
      .map(c => c.name)
      .join(', ');

    const defaultBody = `Hi ${selectedContactNames},

I'm sharing the following documents with you to complete before our appointment:

${documents
  .filter(doc => selectedDocuments.includes(doc.id))
  .map(doc => `• ${doc.name}`)
  .join('\n')}

Please complete these at your earliest convenience. You can access them through your secure client portal.

Thank you!`;

    setEmailBody(defaultBody);
    setStep(3);
  };

  const handleSend = () => {
    onShare(selectedContacts, selectedDocuments, emailBody);
    onClose();
  };

  const handleClose = () => {
    setStep(1);
    setSelectedContacts(contacts.length === 1 ? [contacts[0].id] : []);
    setSelectedDocuments([]);
    setEmailBody('');
    onClose();
  };

  if (!isOpen) return null;

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'consent': return 'Consent Documents';
      case 'scored': return 'Scored measures';
      case 'questionnaire': return 'Questionnaires';
      case 'demographics': return 'Demographics and Credit Card Forms';
      default: return category;
    }
  };

  const groupedDocuments = documents.reduce((acc, doc) => {
    if (!acc[doc.category]) {
      acc[doc.category] = [];
    }
    acc[doc.category].push(doc);
    return acc;
  }, {} as Record<string, Document[]>);

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={handleClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-intake-title"
        aria-describedby="share-intake-description"
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: step === 1 ? '600px' : '900px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h2 id="share-intake-title" style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '600',
            color: '#1f2937'
          }}>
            {step === 1 && 'Select whom you want to share items with:'}
            {step === 2 && `Share items for ${clientName}`}
            {step === 3 && `Share items for ${clientName}`}
          </h2>
          <button
            onClick={handleClose}
            aria-label="Close share intake dialog"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#d1d5db'
            }}
          >
            <X size={24} aria-hidden="true" />
          </button>
        </div>

        {/* Step Indicator for steps 2 and 3 */}
        {(step === 2 || step === 3) && (
          <div id="share-intake-description" style={{
            display: 'flex',
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: step === 2 ? '#0066cc' : '#10b981',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                {step === 3 ? <Check size={20} /> : '1'}
              </div>
              <span style={{
                fontSize: '14px',
                fontWeight: step === 2 ? '600' : '400',
                color: step === 2 ? '#1f2937' : '#6b7280'
              }}>
                {contacts.find(c => selectedContacts.includes(c.id))?.name || 'Client'}
              </span>
            </div>

            <div style={{
              width: '40px',
              height: '2px',
              backgroundColor: '#e5e7eb',
              alignSelf: 'center'
            }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: step === 3 ? '#0066cc' : '#e5e7eb',
                color: step === 3 ? 'white' : '#6b7280',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                2
              </div>
              <span style={{
                fontSize: '14px',
                fontWeight: step === 3 ? '600' : '400',
                color: step === 3 ? '#1f2937' : '#6b7280'
              }}>
                Compose Email
              </span>
            </div>

            <div style={{
              width: '40px',
              height: '2px',
              backgroundColor: '#e5e7eb',
              alignSelf: 'center'
            }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: '#e5e7eb',
                color: '#d1d5db',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                3
              </div>
              <span style={{
                fontSize: '14px',
                color: '#d1d5db'
              }}>
                Review & Send
              </span>
            </div>
          </div>
        )}

        {/* Body */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px'
        }}>
          {/* Description for step 1 */}
          {step === 1 && (
            <p id="share-intake-description" style={{ fontSize: '14px', color: '#d1d5db', marginBottom: '16px', marginTop: 0 }}>
              Choose contacts to share intake documents with
            </p>
          )}
          {/* Step 1: Contact Selection */}
          {step === 1 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px'
            }}>
              {contacts.map(contact => (
                <div
                  key={contact.id}
                  onClick={() => toggleContact(contact.id)}
                  style={{
                    border: '2px solid',
                    borderColor: selectedContacts.includes(contact.id) ? '#10b981' : '#e5e7eb',
                    borderRadius: '12px',
                    padding: '24px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px',
                    position: 'relative',
                    backgroundColor: selectedContacts.includes(contact.id) ? '#f0fdf4' : 'white',
                    transition: 'all 0.2s'
                  }}
                >
                  {/* Checkmark */}
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor: selectedContacts.includes(contact.id) ? '#10b981' : '#d1d5db',
                    backgroundColor: selectedContacts.includes(contact.id) ? '#10b981' : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {selectedContacts.includes(contact.id) && (
                      <Check size={16} color="white" />
                    )}
                  </div>

                  {/* Avatar */}
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: '#94a3b8',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '32px',
                    fontWeight: '600'
                  }}>
                    {contact.initials}
                  </div>

                  {/* Name */}
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1f2937',
                    textAlign: 'center'
                  }}>
                    {contact.name}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 2: Document Selection */}
          {step === 2 && (
            <div>
              {/* Portal Access Notice */}
              {contacts.filter(c => selectedContacts.includes(c.id)).some(c => c.hasPortalAccess) && (
                <div style={{
                  backgroundColor: '#d1fae5',
                  border: '1px solid #10b981',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <Check size={20} color="#10b981" />
                  <span style={{ fontSize: '14px', color: '#065f46' }}>
                    {contacts.find(c => selectedContacts.includes(c.id))?.name} has Client Portal access and can view and complete any item shared.
                  </span>
                </div>
              )}

              {/* Contact Info */}
              <div style={{
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                {contacts.filter(c => selectedContacts.includes(c.id)).map(contact => (
                  <div key={contact.id} style={{ fontSize: '14px', color: '#374151' }}>
                    {contact.name} • {contact.email} • {contact.phone}
                  </div>
                ))}
              </div>

              {/* Document Categories */}
              {Object.entries(groupedDocuments).map(([category, docs]) => {
                const categoryDocIds = docs.map(d => d.id);
                const allSelected = categoryDocIds.every(id => selectedDocuments.includes(id));
                const someSelected = categoryDocIds.some(id => selectedDocuments.includes(id));

                return (
                  <div key={category} style={{ marginBottom: '32px' }}>
                    {/* Category Header */}
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#1f2937',
                      marginBottom: '12px',
                      paddingBottom: '8px',
                      borderBottom: '2px solid #e5e7eb'
                    }}>
                      {getCategoryLabel(category)}
                    </div>

                    {/* Documents Table */}
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <th style={{ textAlign: 'left', padding: '12px 8px', fontSize: '13px', fontWeight: '600', color: '#d1d5db' }}>
                            <input
                              type="checkbox"
                              checked={allSelected}
                              ref={(el) => {
                                if (el) el.indeterminate = someSelected && !allSelected;
                              }}
                              onChange={() => toggleAllInCategory(category)}
                              style={{ marginRight: '8px', cursor: 'pointer' }}
                            />
                          </th>
                          <th style={{ textAlign: 'left', padding: '12px 8px', fontSize: '13px', fontWeight: '600', color: '#d1d5db' }}>
                            {category === 'scored' ? 'Scored measures' : 'Name'}
                          </th>
                          {category === 'scored' && (
                            <th style={{ textAlign: 'left', padding: '12px 8px', fontSize: '13px', fontWeight: '600', color: '#d1d5db' }}>
                              Frequency
                            </th>
                          )}
                          {category === 'scored' && (
                            <th style={{ textAlign: 'left', padding: '12px 8px', fontSize: '13px', fontWeight: '600', color: '#d1d5db' }}>
                              Last shared
                            </th>
                          )}
                          {category !== 'scored' && (
                            <th style={{ textAlign: 'left', padding: '12px 8px', fontSize: '13px', fontWeight: '600', color: '#d1d5db' }}>
                              Shared On
                            </th>
                          )}
                          <th style={{ textAlign: 'left', padding: '12px 8px', fontSize: '13px', fontWeight: '600', color: '#d1d5db' }}>
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {docs.map(doc => (
                          <tr key={doc.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '12px 8px' }}>
                              <input
                                type="checkbox"
                                checked={selectedDocuments.includes(doc.id)}
                                onChange={() => toggleDocument(doc.id)}
                                style={{ cursor: 'pointer' }}
                              />
                            </td>
                            <td style={{ padding: '12px 8px', fontSize: '14px', color: '#1f2937' }}>
                              {doc.name}
                            </td>
                            {category === 'scored' && (
                              <td style={{ padding: '12px 8px', fontSize: '14px', color: '#d1d5db' }}>
                                {doc.frequency}
                              </td>
                            )}
                            {category === 'scored' && (
                              <td style={{ padding: '12px 8px', fontSize: '14px', color: '#d1d5db' }}>
                                {doc.lastShared}
                              </td>
                            )}
                            {category !== 'scored' && (
                              <td style={{ padding: '12px 8px', fontSize: '14px', color: '#d1d5db' }}>
                                {doc.sharedOn || 'Not sent'}
                              </td>
                            )}
                            <td style={{ padding: '12px 8px', fontSize: '14px', color: doc.status?.includes('Completed') || doc.status?.includes('Signed') ? '#10b981' : '#6b7280' }}>
                              {doc.status}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}

          {/* Step 3: Email Composition */}
          {step === 3 && (
            <div>
              <div style={{
                backgroundColor: '#d1fae5',
                border: '1px solid #10b981',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <Check size={20} color="#10b981" />
                <span style={{ fontSize: '14px', color: '#065f46' }}>
                  Selected {selectedDocuments.length} document{selectedDocuments.length !== 1 ? 's' : ''} to share
                </span>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Email Message
                </label>
                <textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={12}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                  placeholder="Compose your email message..."
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <button
            onClick={handleClose}
            style={{
              padding: '10px 20px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: 'white',
              color: '#374151',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>

          {step === 1 && (
            <button
              onClick={handleContinueFromStep1}
              disabled={selectedContacts.length === 0}
              style={{
                padding: '10px 24px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: selectedContacts.length === 0 ? '#d1d5db' : '#0066cc',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: selectedContacts.length === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              Continue to Share
            </button>
          )}

          {step === 2 && (
            <button
              onClick={handleContinueToEmail}
              disabled={selectedDocuments.length === 0}
              style={{
                padding: '10px 24px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: selectedDocuments.length === 0 ? '#d1d5db' : '#0066cc',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: selectedDocuments.length === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              Continue to Email
            </button>
          )}

          {step === 3 && (
            <button
              onClick={handleSend}
              style={{
                padding: '10px 24px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: '#0066cc',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Send Email
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareIntakeModal;
